import getLang from 'I18n/utils/getLang';
import enviro from 'enviro';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LanguageNames from 'ui-addon-i18n/constants/LanguageNames';
import { PUBLIC_LANGUAGES } from '../../constants/Languages';
import { getFullPublicUrl, getPublicLocalizedPathname } from '../../utils/urls';
import { MARKETING_NAV_CDN_PROD_URL, MARKETING_NAV_CDN_QA_URL, MARKETING_NAV_CDN_SCRIPT_ID } from './MarketingNav.constants';
import { getMarketingEmbedNavLocale } from './MarketingNav.utils';
export function useMarketingNavQa(_useMarketingNavCdnQa = false) {
  return useRef(enviro.isQa() && (_useMarketingNavCdnQa || /_marketingNavCdnQa=true/i.test(window.location.search) || /_marketingEmbedNavQa=true/i.test(window.location.search))).current;
}
export function useMarketingNavCdnScript({
  _useMarketingNavCdnQa = false
}) {
  const useMarketingNavCdnQa = useMarketingNavQa(_useMarketingNavCdnQa);
  const [marketingNavCdnRequestState, setMarketingNavCdnRequestState] = useState('PENDING');
  useEffect(() => {
    const handleMarketingNavCdnLoad = () => {
      setMarketingNavCdnRequestState('SUCCEEDED');
    };
    const handleMarketingNavCdnFailed = () => {
      setMarketingNavCdnRequestState('FAILED');
    };
    let marketingNavCdnScript = document.getElementById(MARKETING_NAV_CDN_SCRIPT_ID);
    if (!marketingNavCdnScript) {
      marketingNavCdnScript = document.createElement('script');
      marketingNavCdnScript.setAttribute('src', useMarketingNavCdnQa ? MARKETING_NAV_CDN_QA_URL : MARKETING_NAV_CDN_PROD_URL);
      marketingNavCdnScript.setAttribute('id', MARKETING_NAV_CDN_SCRIPT_ID);
      document.body.appendChild(marketingNavCdnScript);
    }
    marketingNavCdnScript.addEventListener('load', handleMarketingNavCdnLoad);
    marketingNavCdnScript.addEventListener('error', handleMarketingNavCdnFailed);
    return () => {
      if (marketingNavCdnScript) {
        marketingNavCdnScript.removeEventListener('load', handleMarketingNavCdnLoad);
        marketingNavCdnScript.removeEventListener('error', handleMarketingNavCdnFailed);
      }
    };
  }, [useMarketingNavCdnQa]);
  useEffect(() => {
    const marketingNavCdnScript = document.getElementById(MARKETING_NAV_CDN_SCRIPT_ID);
    return () => {
      if (marketingNavCdnScript) {
        marketingNavCdnScript.remove();
      }
    };
  }, []);
  return {
    requestState: marketingNavCdnRequestState
  };
}
export function useMarketingNavLanguagesContent({
  location
}) {
  const {
    pathname,
    search
  } = location;
  const currentLang = getLang();
  return useMemo(() => {
    return PUBLIC_LANGUAGES.reduce((acc, lang) => {
      if (lang in LanguageNames) {
        const localizedPathname = `${getPublicLocalizedPathname(lang)}${pathname.replace(/^\/(de|es|fr|it|ja|nl|fi|da|no|pl|sv|pt|zh-tw|zh-cn|zh|th|ko)\/?/, '')}`.replace(/\/$/, '');
        const languageName = LanguageNames[lang];
        let languageKey = lang;
        if (lang === 'ja') {
          languageKey = 'jp';
        } else if (lang === 'zh-tw' && currentLang === 'zh') {
          languageKey = 'zh';
        }
        acc[languageKey] = [languageName, getFullPublicUrl(), `${localizedPathname}${search}`];
      }
      return acc;
    }, {});
  }, [currentLang, pathname, search]);
}
export function useMarketingEmbedNavLanguages({
  location
}) {
  const {
    pathname,
    search
  } = location;
  return useMemo(() => {
    return PUBLIC_LANGUAGES.reduce((acc, lang) => {
      if (lang in LanguageNames) {
        const localizedPathname = `${getPublicLocalizedPathname(lang)}${pathname.replace(/^\/(de|es|fr|it|ja|nl|fi|da|no|pl|sv|pt|zh-tw|zh-cn|zh|th|ko)\/?/, '')}`.replace(/\/$/, '');
        const languageName = LanguageNames[lang];
        acc.push({
          locale: getMarketingEmbedNavLocale(lang),
          name: languageName,
          url: `${getFullPublicUrl()}${localizedPathname}${search}`
        });
      }
      return acc;
    }, []);
  }, [pathname, search]);
}
export function useLoadMarketingEmbedNavAsset({
  assetId,
  assetType,
  assetUrl,
  initialLoading = true
}) {
  const [{
    loading,
    error
  }, setState] = useState({
    loading: initialLoading,
    error: null
  });
  const addEventListeners = useCallback(element => {
    element.addEventListener('load', () => {
      element.setAttribute('data-loaded', 'true');
      setState({
        loading: false,
        error: null
      });
    });
    element.addEventListener('error', event => {
      setState({
        loading: false,
        error: event.error
      });
    });
  }, []);
  const setLoaded = useCallback(() => {
    setState({
      loading: false,
      error: null
    });
  }, []);
  useEffect(() => {
    if (!loading) {
      return;
    }
    const existingAsset = document.getElementById(assetId);
    if (existingAsset) {
      if (existingAsset.hasAttribute('data-loaded')) {
        setLoaded();
        return;
      }
      addEventListeners(existingAsset);
      return;
    }
    let asset;
    if (assetType === 'css') {
      asset = document.createElement('link');
      asset.href = assetUrl;
      asset.rel = 'stylesheet';
    } else {
      asset = document.createElement('script');
      asset.src = assetUrl;
    }
    asset.id = assetId;
    addEventListeners(asset);
    document.head.appendChild(asset);
  }, [addEventListeners, assetId, assetType, assetUrl, loading, setLoaded]);
  return {
    loading,
    error
  };
}